<template>
  <div>
    <div v-if="!allowed" class="p-m-3 pse-note error"><i class="pi pi-ban"></i><span>You are not allowed to create a new enquiry.</span></div>
    <!-- Design D: today's "Enquiry Details" dialog, organised in three tabs, with one Save in a footer that always shows -->
    <Dialog v-else :visible="true" :style="{ width: '820px' }" :modal="true" class="p-fluid pse-d" @update:visible="close">
      <template #header>
        <h5>Enquiry Details</h5>
      </template>

      <LayoutCompareNote />
      <TabView>
        <TabPanel>
          <template #header>
            <span>1 Project &amp; enquiry</span>
            <span v-if="tabMissing[0].n" class="pse-d-count">{{ tabMissing[0].n }}</span>
          </template>
          <div class="p-formgrid p-grid">
            <div class="p-field p-col">
              <label for="pseDProject">Project *</label>
              <div class="p-inputgroup">
                <InputText id="pseDProject" :value="project ? project.name : ''" placeholder="Click button to search for project" :disabled="true"
                  :class="{ 'p-invalid': submitted && !project }" />
                <Button icon="pi pi-search" class="p-button-warning" :disabled="fixedJob" @click="projectSearch = true" />
              </div>
            </div>
            <div class="p-field p-col">
              <label for="pseDPhase">Phase</label>
              <div class="p-inputgroup">
                <Dropdown id="pseDPhase" v-model="phaseId" :options="phaseOptions" optionLabel="label" optionValue="phaseId"
                  placeholder="Main project" :disabled="!project || fixedJob" />
                <Button icon="pi pi-plus" class="p-button-warning" title="Add a phase" :disabled="!project || fixedJob" @click="phaseDialog = true" />
              </div>
            </div>
          </div>
          <div class="p-formgrid p-grid">
            <div class="p-field p-col">
              <label>Postcode Area</label>
              <InputText :value="project ? project.postcodeArea : ''" :disabled="true" />
            </div>
            <div class="p-field p-col">
              <label>Post Code</label>
              <InputText :value="project ? project.postCode : ''" :disabled="true" />
            </div>
            <div class="p-field p-col">
              <label>City/Town</label>
              <InputText :value="project ? project.town : ''" :disabled="true" />
            </div>
          </div>
          <div v-if="sharedEnqId" class="p-mb-3 job-locked-note">
            <i class="pi pi-lock"></i>
            This enquiry is already in the CRM ({{ existingBidCount }} quotation{{ existingBidCount === 1 ? '' : 's' }}): its details are shared,
            so this adds a quotation to it.
          </div>
          <JobFields v-model="job" :settings="settings" :submitted="submitted && !sharedEnqId" :disabled="!!sharedEnqId" colClass="p-col-6" />
        </TabPanel>

        <TabPanel>
          <template #header>
            <span>2 Quotation</span>
            <span v-if="tabMissing[1].n" class="pse-d-count">{{ tabMissing[1].n }}</span>
          </template>
          <QuoteFields :form="this" col="p-col-6" :inlineCheck="false" />
        </TabPanel>

        <TabPanel header="3 Documents">
          <AttachmentsByCategory v-if="!sharedEnqId" ref="attachments" :sharedEnqId="null" />
          <AttachmentsByCategory v-else :key="sharedEnqId" :sharedEnqId="sharedEnqId" />
        </TabPanel>
      </TabView>

      <div v-if="error" class="pse-note error"><i class="pi pi-times-circle"></i><span>{{ error }}</span></div>

      <template #footer>
        <div class="pse-d-footer">
          <span v-if="missingText" class="pse-d-missing">{{ missingText }}</span>
          <span v-else class="pse-d-ok"><i class="pi pi-check"></i> Nothing missing</span>
          <span class="pse-spacer"></span>
          <Button label="Cancel" icon="pi pi-times" class="p-button-danger" style="width: 100px;" @click="close" />
          <Button v-if="canOffer" label="Save" icon="pi pi-check" class="p-button-success pse-save" style="width: 100px;" :loading="saving" @click="save" />
        </div>
      </template>
    </Dialog>

    <DuplicateQuestion :visible="questionOpen" :check="check" :custName="customer && customer.custName ? customer.custName : ''"
      @newVersion="answerNewVersion" @anyway="answerAnyway" @change="answerChangeCustomer" />

    <Dialog header="Search Project" :visible="projectSearch" :modal="true" :style="{ width: '900px' }" @update:visible="projectSearch = $event">
      <ProjectPicker enquiryAction="Add a quotation" @project="onProject" @sharedEnquiry="onExistingEnquiry" />
    </Dialog>

    <Dialog header="New Phase" :visible="phaseDialog" :modal="true" :style="{ width: '420px' }" class="p-fluid" @update:visible="phaseDialog = $event">
      <div class="p-formgrid p-grid">
        <div class="p-field p-col-4">
          <label for="pseDPhNo">Phase No *</label>
          <InputText id="pseDPhNo" v-model.trim="newPhase.phaseNo" placeholder="e.g. 2A" />
        </div>
        <div class="p-field p-col-8">
          <label for="pseDPhDesc">Description</label>
          <InputText id="pseDPhDesc" v-model.trim="newPhase.description" placeholder="e.g. Annex block" />
        </div>
      </div>
      <small class="pse-muted">The main project counts as the same enquiry as each of its phases for the duplicate check.</small>
      <template #footer>
        <Button label="Add" icon="pi pi-check" class="p-button-success" style="width: 100px;" :disabled="!newPhase.phaseNo" :loading="busy" @click="addPhase" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import ProjectPicker from './ProjectPicker.vue'
import JobFields from './JobFields.vue'
import AttachmentsByCategory from './AttachmentsByCategory.vue'
import QuoteFields from './QuoteFields.vue'
import DuplicateQuestion from './DuplicateQuestion.vue'
import LayoutCompareNote from './LayoutCompareNote.vue'
import jobQuoteForm from './jobQuoteForm'
import { missingCounts } from './sharedEnquiryLogic'

// Design D of the New Enquiry form (Mohammed 18/10): design B's dialog and logic, organised in tabs, with the missing
// mandatory fields counted per tab in a footer that always shows.
export default {
  name: 'NewEnquiryTabbed',
  components: { ProjectPicker, JobFields, AttachmentsByCategory, QuoteFields, DuplicateQuestion, LayoutCompareNote },
  mixins: [jobQuoteForm],
  computed: {
    projectInSettings() { return this.settings.some(s => s.fieldKey === 'project' && s.mandatory) },
    tabMissing() {
      return [
        // the project is always needed; the settings may list it too, so it is counted once
        { tab: 'Project & enquiry', n: this.jobMissing.length + (!this.project && !this.projectInSettings ? 1 : 0) },
        { tab: 'Quotation', n: this.missing.length }
      ]
    },
    missingText() { return missingCounts(this.tabMissing) }
  }
}
</script>

<style scoped>
@import './sharedEnquiry.css';
.job-locked-note { background: #fff8e1; color: #8a5a00; border: 1px solid #ffcc80; border-radius: 4px; padding: 8px 12px; font-size: 13px; }
.pse-d-footer { display: flex; align-items: center; gap: 8px; }
.pse-d-missing { color: #c62828; font-weight: 600; }
.pse-d-ok { color: #2e7d32; }
.pse-d-count { margin-left: 6px; background: #c62828; color: #fff; border-radius: 10px; padding: 0 7px; font-size: 12px; }
</style>

<style>
/* not scoped: the Dialog's own element carries no scope attribute */
.pse-d .p-dialog-content { max-height: 70vh; overflow-y: auto; }
</style>
